import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/core/toaster";
import { contactsKeys } from "./use-contacts";
import { campaignsKeys } from "./use-campaigns";

export interface CallLog {
	conversationId: string;
	agent: any;
	agentId: string;
	campaign: any;
	contactNumber: string;
	createdAt: string;
	externalId: string;
	id: string;
	isOutbound: boolean;
	metadata: any;
	originNumber: string;
	status: number;
	/** Who was on the call. Present on lists so a page of calls can name people. */
	contact?: { id: string; phone?: string; attributes?: Record<string, any> } | null;
}

export interface Conversation {
	id: string;
	conversationId: string;
	role: string;
	content: string;
	toolCalls: any;
	toolCallId: any;
	usage: any;
	createdAt: string;
	conversation: any;
	completion: string;
	metadata: any;
}

export const callLogsKeys = {
	all: ["call-logs"] as const,
	lists: () => [...callLogsKeys.all, "list"] as const,
	list: (query?: string) => [...callLogsKeys.lists(), { query }] as const,
	details: () => [...callLogsKeys.all, "detail"] as const,
	detail: (id: string) => [...callLogsKeys.details(), id] as const,
	stats: (campaignId?: string) => [...callLogsKeys.all, "stats", ...(campaignId ? [campaignId] : [])] as const,
};

export function useCallLogsList(query?: string) {
	return useQuery({
		queryKey: callLogsKeys.list(query),
		queryFn: async () => {
			const queryString = query ? `?${query}` : "";
			const response = await apiClient.get(`/calls${queryString}`);
			return response.data;
		},
	});
}

export function useCallLog(id: string) {
	return useQuery({
		queryKey: callLogsKeys.detail(id),
		queryFn: async () => {
			const response = await apiClient.get(`/calls/${id}`);
			return response.data;
		},
		enabled: !!id,
	});
}

export function useCallLogsStats(campaignId?: string) {
	return useQuery({
		queryKey: callLogsKeys.stats(campaignId),
		queryFn: async () => {
			const response = await apiClient.get(`/calls-statistics${campaignId ? `?campaignId=${campaignId}` : ""}`);
			return response.data;
		},
		refetchOnMount: "always",
	});
}

export function useMarkCallStuck() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (callId: string) => {
			const response = await apiClient.post(`/calls/${callId}/stuck`);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: callLogsKeys.lists() });
			queryClient.invalidateQueries({ queryKey: contactsKeys.calls() });
			queryClient.invalidateQueries({ queryKey: campaignsKeys.calls() });
			toast.success("Call marked as stuck");
		},
		onError: (error: any) => {
			const message = error?.response?.data?.message;
			toast.error(Array.isArray(message) ? message[0] : message || "Failed to mark call as stuck");
		},
	});
}
